/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  TouchableOpacity,
  View,
  ActivityIndicator,
  Linking,
  Platform,
  Alert,
  BackHandler,
  StyleSheet,
  RefreshControl,
  Modal,
  Animated,
  LayoutAnimation,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useFocusAndAppState from "@hooks/useFocusAndAppState";
import useSarvCall from "@hooks/useSarvCall";
import t from "@constants/localization";
import colors from "@constants/color";
import * as Sentry from "@sentry/react-native";
import Delivery from "@assets/svg/ShopPerformance/TickCircle";
import Pending from "@assets/svg/RRD/pending";
import Rescheduled from "@assets/svg/RRD/rescheduled";
import RtoIcon from "@assets/svg/RRD/Rto";
import Text from "@components/CustomText";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { debounce } from "lodash";
import FiltersIcon from "@assets/svg/Wallet/Filter";
import {
  cancelRto,
  getDeliveryTasks,
  markTouchpointPickedUpByCpdb,
} from "@services/api";
import { useSelector } from "react-redux";
import testIds from "@constants/testIds";
import UpdateAppModal from "@components/UpdateAppModal";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import { ScrollView } from "react-native-gesture-handler";
import { width } from "@constants/metrics";
import analytics from "@services/analytics";
import { MY_TASK_EVENTS } from "@constants/analyticsEventsCT.js";
import DeliveryCard from "./components/DeliveryCard";
import BagsItemsModal from "./components/BagsItemsModal.tsx";
import PickupCard from "./components/PickupCard";
import DelayTag from "./components/DelayTag";
import FilterModal from "./components/FilterModal.tsx";
import EmptyList from "./components/EmptyList";
import ContactModal from "./OrdersMapView/ContactModal.tsx";
import CallOptionsModal from "./components/CallOptionsModal.tsx";
import { contactAllowedCardTypes } from "./OrdersMapView/constants.ts";
import SearchField from "./components/SearchField.tsx";
import SarvCallModal from "./components/SarvCallModal.tsx";

const PATH = "screens/MyTask";

const pageEnum = {
  PENDING: "pending",
  ESCALATED: "escalated",
  DELIVERY: "delivery",
  RTO: "rto",
  RESCHEDULED: "rescheduled",
};

function MyTask({ route }) {
  const initialFilter = { routes: [], dbs: [] };
  const navigation = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(pageEnum.PENDING);
  const [listData, setListData] = useState([]);
  const [pastListData, setPastListData] = useState([]);
  const [headerData, setHeaderData] = useState([]);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  // The filter last applied with Save. selectedFilter is the popup's draft;
  // closing without Save (✕, backdrop, Android back) restores this, so an
  // unsaved pick never shows as selected or reaches a later fetch.
  const appliedFilterRef = useRef(initialFilter);
  const [amountCollected, setAmountCollected] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [contactOption, setContactOption] = useState(null);
  const [showCallOptionsModal, setShowCallOptionsModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [callModalMode, setCallModalMode] = useState("DEFAULT");
  const [showBagsLargeItemsModal, setShowBagsLargeItemsModal] = useState(false);
  const isRescheduledCp = useRef(false);
  const [selectedCategory, setSelectedCategory] = useState("routes");
  const [taskActionConfig, setTaskActionConfig] = useState({});
  const [filterV2, setFilterV2] = useState({});
  const [filterV2Meta, setFilterV2Meta] = useState({});
  const [showUpdateAppModal, setShowUpdateAppModal] = useState(false);
  const { createCall, showSarvModal, setShowSarvModal, updateSarvData } =
    useSarvCall();
  const [contactOpts, setContactOpts] = useState(["DIALER", "WA"]);

  const textInputRef = useRef(null);
  const handleOptionPress = (item) => {
    setSelectedFilter((prev) => ({ ...prev, [selectedCategory]: item }));
  };
  const getSelectedFilters = () => {
    return selectedFilter[selectedCategory] || {};
  };
  const clearFilters = () => {
    setSelectedFilter(initialFilter);
    setSelectedCategory("routes");
    analytics.trackEvent(MY_TASK_EVENTS.CLEAR_FILTER);
  };

  const cp_db_id = useSelector(({ auth }) => auth?.user?.cp_db_id);
  const coords = useSelector(({ auth }) => auth?.coords);

  useFocusAndAppState(
    () => {},
    () => {
      if (isRescheduledCp.current) {
        AsyncStorage.getItem("CARD_ACTION").then((val) => {
          if (!val) return;
          setShowCallOptionsModal(true);
          setContactOption(null);
          setShowSarvModal(false);
          AsyncStorage.removeItem("CARD_ACTION");
        });
      }
    },
  );

  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };

    const backHandlerSub = BackHandler.addEventListener(
      "hardwareBackPress",
      backHandler,
    );

    return () => {
      backHandlerSub.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [currentPage]),
  );

  const filterPayload = useMemo(() => {
    const res = Object.keys(selectedFilter).reduce((acc, key) => {
      if (["routes", "dbs"].includes(key))
        acc[key] = selectedFilter[key]?.key ? [selectedFilter[key]?.key] : [];
      else acc[key] = selectedFilter[key]?.key;

      return acc;
    }, {});
    return res;
  }, [selectedFilter]);

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setIsRefreshing(false);
  };

  const fetchData = async ({ searchTerm = "", isSearch = true } = {}) => {
    setIsLoading(true);
    setShowUpdateAppModal(false);
    try {
      const payload = {
        searchTerm: isSearch ? searchTerm || searchText : "",
        filter: filterPayload,
        coords,
      };
      const { data } = await getDeliveryTasks(payload);
      isRescheduledCp.current = data?.isRescheduledCp || false;
      updateSarvData(data);
      // The backend's returnNeeded tab (include_return_needed, see
      // getDeliveryTasks) has no screen here yet, so it is not offered.
      setHeaderData(
        data?.tasks?.filter((task) => task?.type !== "returnNeeded"),
      );
      setFilterV2(data?.filters_v2);
      setFilterV2Meta(data?.filters_v2_meta);
      setAmountCollected(data?.amount_collected_today);
      setTaskActionConfig(data?.task_action_config);
      setContactOpts(data?.contact_btns || ["DIALER", "WA"]);
      if (currentPage === pageEnum.PENDING) {
        setListData(data?.pending);
        setPastListData([]);
      } else if (currentPage === pageEnum.ESCALATED) {
        setListData(data?.escalated);
        setPastListData([]);
      } else if (currentPage === pageEnum.RESCHEDULED) {
        setListData(data?.rescheduled);
        setPastListData([]);
      } else if (currentPage === pageEnum.DELIVERY) {
        setListData(data?.deliveredToday);
        setPastListData(data?.deliveredPast);
      } else if (currentPage === pageEnum.RTO) {
        setListData(data?.rtoToday);
        setPastListData(data?.rtoPast);
      }
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.error === "UPDATE_APP_FROM_PLAYSTORE") {
        setShowUpdateAppModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onButtonPress = async (key, data) => {
    try {
      if (key === "MULTIPLE_ORDERS") {
        navigation.navigate("CustomerOrdersView", {
          data,
          type: "delivery",
        });
      } else if (key === "CONFIRM_DELIVERY") {
        return navigation.navigate("PreDeliveryItemList", {
          orders_list: data?.order_ids,
          spoke_name: "",
          date: "",
          user_name: data?.user_name,
          user_id: data?.user_id,
          user_phone: data?.user_phone,
          is_delivery: true,
          afterOtpRedirectScreenName: "MyTask",
        });
      } else if (key === "CANCEL_DELIVERY") {
        return navigation.navigate("PreDeliveryItemList", {
          orders_list: data?.order_ids,
          spoke_name: "",
          date: "",
          user_name: data?.user_name,
          user_id: data?.user_id,
          user_phone: data?.user_phone,
          is_delivery: false,
        });
      } else if (key === "CANCEL_RTO") {
        const payload = {
          return_req_ids: data?.return_req_ids,
        };
        await cancelRto(payload);
        onRefresh();
      } else if (key === "MULTIPLE_RTOS") {
        navigation.navigate("CustomerOrdersView", {
          data,
          type: "rto",
        });
      } else if (key === "CONFIRM_PICKUP") {
        const payload = {
          route_id: data.route_id,
          cx_user_id: data.user_id,
          order_ids: data.delivery_pending_order_ids,
        };
        cp_db_id && (await markTouchpointPickedUpByCpdb(payload));
        onRefresh();
      } else if (key === "RESET_PICKUP") {
        const payload = {
          route_id: data.route_id,
          cx_user_id: data.user_id,
          order_ids: [],
        };
        cp_db_id && (await markTouchpointPickedUpByCpdb(payload));
        onRefresh();
      }
    } catch (error) {}
  };
  const openMaps = (latitude, longitude) => {
    try {
      Linking.openURL(
        Platform.OS === "ios"
          ? `https://maps.apple.com/maps?daddr=${latitude},${longitude}`
          : `https://maps.google.com/maps?q=loc:${latitude},${longitude}`,
      );
    } catch (error) {
      Alert.alert(t(`${PATH}/mapsAlert`));
      Sentry.captureException(error);
    }
  };
  const onPressCall = async (key, item) => {
    setSelectedCard(item);
    setCallModalMode(item.action ? "EDIT" : "DEFAULT");
    if (!item?.phone_number_2) {
      if (key === "phone") {
        if (item?.user_id && isRescheduledCp.current) {
          if (
            contactAllowedCardTypes.includes(item.card_type) ||
            item?.action
          ) {
            createCall(
              item?.phone_number_1,
              item?.user_id,
              item?.route_id,
              item?.callmatic_mode,
            );
            return;
          }
          AsyncStorage.setItem(
            "CARD_ACTION",
            JSON.stringify({
              cx_id: item?.user_id,
              action: key,
              cx_phone: item?.phone_number_1,
              created_at: new Date(),
            }),
            () => {
              createCall(
                item?.phone_number_1,
                item?.user_id,
                item?.route_id,
                item?.callmatic_mode,
              );
            },
          );
        } else {
          createCall(
            item?.phone_number_1,
            item?.user_id,
            item?.route_id,
            item?.callmatic_mode,
          );
        }
      } else {
        if (item?.user_id && isRescheduledCp.current) {
          if (
            contactAllowedCardTypes.includes(item.card_type) ||
            item?.action
          ) {
            const url = `whatsapp://send?phone=+91${item?.phone_number_1}`;
            Linking.canOpenURL(url).then(await Linking.openURL(url));
            return;
          }
          AsyncStorage.setItem(
            "CARD_ACTION",
            JSON.stringify({
              cx_id: item.user_id,
              action: "wa",
              cx_phone: item?.phone_number_1,
              created_at: new Date(),
            }),
            async () => {
              const url = `whatsapp://send?phone=+91${item?.phone_number_1}`;
              Linking.canOpenURL(url).then(await Linking.openURL(url));
            },
          );
        } else {
          const url = `whatsapp://send?phone=+91${item?.phone_number_1}`;
          Linking.canOpenURL(url).then(await Linking.openURL(url));
        }
      }
    } else {
      setContactOption(key);
    }
  };

  const handlePressEditCallOptions = (item) => {
    if (isRescheduledCp.current) {
      setSelectedCard(item);
      setShowCallOptionsModal(true);
      setCallModalMode("EDIT");
    }
  };

  const handlePressItemspopup = (item) => {
    setSelectedCard(item);
    setShowBagsLargeItemsModal(true);
  };

  const onSearchInputChange = debounce((text) => {
    setSearchText(text);
    fetchData({
      searchTerm: text,
      isSearch: text.length > 0,
    });
  }, 800);
  const onPressCrossIcon = () => {
    onSearchInputChange("");
  };
  const onPressSearchIcon = () => {
    setIsSearchEnabled(!isSearchEnabled);
  };
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };
  const checkListDataIsEmpty = () => {
    if (isLoading) {
      return false;
    }
    if (listData) {
      return (
        listData?.every((item) => item?.data?.length === 0) &&
        pastListData?.every((item) => item?.data?.length === 0)
      );
    }
    return true;
  };

  const closeFilterModal = () => {
    setSelectedFilter(appliedFilterRef.current);
    setShowFilterModal(false);
  };

  const filterchange = () => {
    appliedFilterRef.current = selectedFilter;
    setShowFilterModal(false);
    fetchData({});
    analytics.trackEvent(MY_TASK_EVENTS.FILTER_APPLIED, {
      filter: JSON.stringify(filterPayload),
    });
  };

  const renderSvg = (type) => {
    switch (type) {
      case "pending":
        return <Pending color="red" />;
      case "escalated":
        return <Pending color="red" />;
      case "rescheduled":
        return <Rescheduled />;
      case "delivery":
        return <Delivery />;
      case "rto":
        return <RtoIcon />;
      default:
        return null;
    }
  };

  const onPressTabBtn = (type) => {
    setCurrentPage(type);
    setListData([]);
    setPastListData([]);
  };
  const numberOfFilters = () => {
    let count = 0;
    Object.keys(selectedFilter).forEach((key) => {
      const increment = Array.isArray(selectedFilter[key])
        ? selectedFilter[key].length > 0
        : selectedFilter[key];
      if (increment) count++;
    });
    return count;
  };

  const fetchSectionData = () => {
    return [
      {
        title:
          currentPage === pageEnum.DELIVERY
            ? t(`${PATH}/todays_delivery`)
            : t(`${PATH}/todays_rto`),
        data: listData,
      },
      {
        title:
          currentPage === pageEnum.DELIVERY
            ? t(`${PATH}/past_delivery`)
            : t(`${PATH}/past_rto`),
        data: pastListData,
      },
    ];
  };
  const searchHeight = useRef(new Animated.Value(45)).current;
  const scrollY = useRef(new Animated.Value(0)).current;
  const scrollDirection = useRef(0);
  const lastScrollY = useRef(0);

  const onScroll = Animated.event(
    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
    { useNativeDriver: false },
  );
  useEffect(() => {
    const listener = scrollY.addListener(({ value }) => {
      if (value > lastScrollY.current) {
        scrollDirection.current = 1;
      } else if (value < lastScrollY.current) {
        scrollDirection.current = -1;
      }
      lastScrollY.current = value;
    });

    return () => scrollY.removeListener(listener);
  }, [scrollY]);

  const toggleSearchVisibility = (visible) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    Animated.parallel([
      Animated.timing(searchHeight, {
        toValue: visible ? 45 : 0,
        duration: 200,
        useNativeDriver: false,
      }),
    ]).start();
  };

  const handleScroll = () => {
    if (
      scrollDirection.current === 1 &&
      searchText.length === 0 &&
      numberOfFilters() === 0
    ) {
      toggleSearchVisibility(false);
    } else if (
      scrollDirection.current === -1 &&
      searchText.length === 0 &&
      numberOfFilters() === 0
    ) {
      toggleSearchVisibility(true);
    }
  };

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const renderItem = useCallback(
    ({ item }) => {
      if (item?.pickups) {
        return (
          <PickupCard
            item={item}
            onButtonPress={onButtonPress}
            onPressCall={onPressCall}
            openMaps={openMaps}
            onPickupCardPress={() => {}}
            handlePressEditCallOptions={handlePressEditCallOptions}
          />
        );
      }
      return (
        <DeliveryCard
          item={item}
          onPressCall={onPressCall}
          openMaps={openMaps}
          onPressBtn={onButtonPress}
          isRescheduledCp={isRescheduledCp.current}
          handlePressItemspopup={handlePressItemspopup}
          handlePressEditCallOptions={handlePressEditCallOptions}
          taskActionConfig={taskActionConfig}
          contactOpts={contactOpts}
        />
      );
    },
    [taskActionConfig],
  );

  const renderPageType = (item) => {
    return (
      <TouchableOpacity
        key={item.type}
        testID="mt-tab-btn"
        onPress={() => onPressTabBtn(item.type)}
        disabled={item.type === currentPage}
        style={[
          styles.typeContainer,
          {
            minWidth: width / (isRescheduledCp.current ? 3.4 : 3) - 15,
            height: 60,
            backgroundColor:
              item.type === currentPage ? colors.black : colors.white,
          },
        ]}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {renderSvg(item.type)}
          <Text
            newfont={true}
            variant="body_text_1"
            style={{
              marginLeft: 4,
              color: item.type === currentPage ? colors.white : colors.black,
            }}
          >
            {item?.title}
          </Text>
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Text
            newfont={true}
            variant="title_16"
            style={{
              marginLeft: 16,
              marginRight: 8,
              color: item.type === currentPage ? colors.white : colors.black,
            }}
          >
            {item.count}
          </Text>
          {item.delayCount > 0 && item?.type === pageEnum.PENDING && (
            <DelayTag delay={`Delay ${item.delayCount}`} />
          )}
        </View>
      </TouchableOpacity>
    );
  };
  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={
          !isSearchEnabled ? t(`${PATH}/heading`) : t(`SearchOrder/title`)
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        isSearchEnabled={isSearchEnabled}
        isMapEnabled={true}
        mapScreen="OrdersMapView"
        searchPlaceholder={
          filterV2Meta?.searchTermPlaceholder || t(`SearchOrder/placeholder`)
        }
        onPressCrossIcon={onPressCrossIcon}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        onSearchInputChange={onSearchInputChange}
        onPressSearchIcon={onPressSearchIcon}
      />
      <Animated.View style={{ height: searchHeight }}>
        {searchHeight !== 0 && (
          <View
            style={{
              flexDirection: "row",
              paddingTop: 10,
              marginHorizontal: 12,
            }}
          >
            <SearchField
              searchPlaceholder={filterV2Meta?.searchTermPlaceholder}
              searchText={searchText}
              setSearchText={setSearchText}
              onPressCrossIcon={onPressCrossIcon}
              onSearchInputChange={onSearchInputChange}
              testID="mt-search-field"
              hideShadow={false}
              textInputRef={textInputRef}
            />
            <TouchableOpacity
              testID="mt-filter"
              onPress={() => setShowFilterModal(true)}
              activeOpacity={0.7}
              style={styles.filterContainer}
            >
              {numberOfFilters() > 0 && (
                <View style={styles.filterBadge}>
                  <Text
                    variant="caption_1"
                    style={{
                      color: colors.white,
                      fontSize: 13,
                      lineHeight: 18,
                    }}
                  >
                    {numberOfFilters()}
                  </Text>
                </View>
              )}
              <FiltersIcon />
              <Text variant="body_text_3" style={styles.filterText}>
                {filterV2Meta?.title}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </Animated.View>
      <View
        style={{
          height: 85,
          paddingVertical: 10,
          backgroundColor: colors.primaryBg,
        }}
      >
        <ScrollView
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          style={styles.pagetypeheader}
        >
          {headerData.map(renderPageType)}
          <View style={{ width: 10 }} />
        </ScrollView>
      </View>

      {checkListDataIsEmpty() ? (
        <EmptyList type={currentPage} searchTerm={searchText} />
      ) : (
        <Animated.SectionList
          sections={fetchSectionData()}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
          }
          renderItem={({ item, index }) => (
            <View key={keyExtractor(item, index)}>
              {renderItem({ item, index })}
            </View>
          )}
          renderSectionHeader={({ section: { title, data } }) =>
            ![
              pageEnum.PENDING,
              pageEnum.ESCALATED,
              pageEnum.RESCHEDULED,
            ].includes(currentPage) &&
            data?.length > 0 && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginHorizontal: 16,
                }}
              >
                <Text
                  newfont={true}
                  variant="title_16"
                  style={{ marginVertical: 8, marginRight: 10 }}
                >
                  {title}
                </Text>
                <View
                  style={{
                    flex: 1,
                    height: 0.5,
                    backgroundColor: colors.lightMediumBlack,
                  }}
                />
              </View>
            )
          }
          keyExtractor={(item, index) => keyExtractor(item, index)}
          renderSectionFooter={() => <View style={{ height: 40 }} />}
          onScroll={onScroll}
          onScrollEndDrag={handleScroll}
        />
      )}
      {currentPage === pageEnum.DELIVERY && amountCollected > 0 && (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            position: "absolute",
            bottom: 0,
            backgroundColor: colors.plain_green,
            width: "100%",
          }}
        >
          <Text
            variant="subtitle_16"
            style={{ color: colors.white, margin: 10, marginLeft: 16 }}
          >
            {t(`${PATH}/amountCollected`)}
          </Text>

          <Text
            variant="heading_24"
            style={{ color: colors.white, margin: 8, marginRight: 16 }}
          >
            ₹ {amountCollected}
          </Text>
        </View>
      )}
      <BagsItemsModal
        selectedCard={selectedCard}
        showBagsLargeItemsModal={showBagsLargeItemsModal}
        setShowBagsLargeItemsModal={setShowBagsLargeItemsModal}
      />
      <ContactModal
        contactOption={contactOption}
        setContactOption={setContactOption}
        selectedMarker={selectedCard}
        isRescheduledCp={isRescheduledCp.current}
        storageKey="CARD_ACTION"
        createCall={createCall}
      />
      <CallOptionsModal
        selectedMarker={selectedCard}
        visible={isRescheduledCp.current && showCallOptionsModal}
        callModalMode={callModalMode}
        onClose={() => setShowCallOptionsModal(false)}
        setShowCallOptionsModal={setShowCallOptionsModal}
        setContactOption={setContactOption}
        refreshData={fetchData}
        taskActionConfig={taskActionConfig}
      />
      <FilterModal
        visible={showFilterModal}
        // Not onClose={setShowFilterModal}: the ✕ and the backdrop call
        // onClose(pressEvent), which set the state to the (truthy) event and
        // left the modal open. Closing without Save discards the draft.
        onClose={closeFilterModal}
        onSave={filterchange}
        filterData={filterV2}
        currentFilter={selectedFilter}
        handleOptionPress={handleOptionPress}
        getSelectedFilters={getSelectedFilters}
        clearFilters={clearFilters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        metadata={filterV2Meta}
      />
      <Modal visible={isLoading} transparent={true}>
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </Modal>
      <SarvCallModal
        showSarvModal={showSarvModal}
        setShowSarvModal={setShowSarvModal}
      />
      <UpdateAppModal forceVisible={showUpdateAppModal} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.primaryBg },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },

  typeContainer: {
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 6,
    alignItems: "flex-start",
    elevation: 4,
    marginTop: 2,
    marginLeft: 10,
    backgroundColor: colors.white,
  },
  pagetypeheader: {
    height: 60,
  },
  filterContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2FBF7",
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 4,
    borderColor: "#CCDFD6",
    borderWidth: 1,
  },
  filterText: {
    marginLeft: 4,
  },
  filterBadge: {
    backgroundColor: "#4274EA",
    padding: 2,
    borderRadius: 20,
    position: "absolute",
    top: -8,
    right: -8,
    minWidth: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default MyTask;
